import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSpinner,
  faSyncAlt,
  faTableList,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";
import TransactionDetailsView from "./TransactionDetailsView";

const TransactionDetailsPage = () => {
  const navigate = useNavigate();
  const { transNo } = useParams();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  const fetchDetails = useCallback(async () => {
    if (!transNo) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/transactions/${encodeURIComponent(transNo)}/details`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      if (response.data.status === "success") {
        setDetails(response.data.data || {});
      } else {
        toast.error(response.data.message || "Failed to load details.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (error.response?.status === 403) {
        toast.error("You do not have permission to access this.");
        return;
      }
      if (error.response?.status === 429) {
        toast.error("Rate limit exceeded. Please try again later.");
        return;
      }
      toast.error("Failed to fetch transaction details.");
    } finally {
      setLoading(false);
    }
  }, [transNo]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/transactions")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              aria-label="Back to transactions"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h1 className="text-lg font-bold text-slate-950">
                Transaction Details
              </h1>
              <p className="text-sm text-slate-500">
                {transNo || "-"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={fetchDetails}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <FontAwesomeIcon
                icon={loading ? faSpinner : faSyncAlt}
                className={`mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => navigate("/transactions")}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              <FontAwesomeIcon icon={faTableList} className="mr-2" />
              Transaction List
            </button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-5">
        {loading && !details ? (
          <div className="flex h-64 items-center justify-center rounded-lg border border-slate-200 bg-white">
            <FontAwesomeIcon
              icon={faSpinner}
              className="mr-3 animate-spin text-2xl text-indigo-600"
            />
            <span className="text-sm font-semibold text-slate-700">
              Loading transaction details...
            </span>
          </div>
        ) : details ? (
          <TransactionDetailsView data={details} />
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-600">
            No transaction details available.
          </div>
        )}
      </main>
    </div>
  );
};

export default TransactionDetailsPage;
